import React, {
  useContext,
  useEffect,
  useRef,
  createContext,
} from "react";
import { createPortal } from "react-dom";

import styles from "./Modal.module.scss";
import { FiX } from "react-icons/fi";

export const ModalContext = createContext(null);

export const ModalHeader = ({ children }) => (
  <div id="modal-header" className={`${styles["modal-content__header"]}`}>
    <div className={`${styles["modal-content__header--title"]}`}>{children}</div>
    <ModalClose />
  </div>
);

/**
 * Dismiss control; reads Modal’s onClose from context unless onClick is passed.
 * Rendered in ModalHeader by default; use inside ModalBody with showCloseButton for body-only layouts.
 */
export const ModalClose = ({
  onClick,
  className = "",
  iconSize = 18,
  ariaLabel = "Close modal",
  children,
  ...buttonProps
}) => {
  const ctx = useContext(ModalContext);
  const handler = onClick ?? ctx?.onClose;
  if (!handler) return null;

  return (
    <button
      type="button"
      className={`${styles["modal-content__header--close"]} ${className}`.trim()}
      onClick={handler}
      aria-label={ariaLabel}
      {...buttonProps}
    >
      {children ?? <FiX size={iconSize} />}
    </button>
  );
};

const ModalDialogContent = ({
  children,
  width,
  scrollBehavior,
  className = "",
}) => {
  const modalContentRef = useRef(null);

  return (
    <div
      ref={modalContentRef}
      className={`${className && className} ${styles["modal-content"]} ${
        styles[`modal-content--${width || "medium"}`]
      } ${styles[`modal-content--scroll-${scrollBehavior || ""}`]}`}
    >
      {children}
    </div>
  );
};

export const Modal = ({
  isOpen,
  width,
  scrollBehavior,
  onClose,
  children,
  className = "",
}) => {
  const modalRef = useRef(null);
  const zIndexRef = useRef(null);

  useEffect(() => {
    if (isOpen) zIndexRef.current = nextModalZIndex();
  }, [isOpen]);

  if (isOpen) {
    const z = zIndexRef.current ?? nextModalZIndex();
    const node = (
      <div
        ref={modalRef}
        className={styles["modal-container"]}
        style={{ zIndex: z }}
      >
        <ModalContext.Provider value={{ onClose: onClose ?? null }}>
          <ModalDialogContent
            width={width}
            scrollBehavior={scrollBehavior}
            className={className}
          >
            {children}
          </ModalDialogContent>
        </ModalContext.Provider>

        {scrollBehavior === "outside" && (
          <div
            style={{ position: "absolute", width: "100%", marginTop: "100%" }}
          >
            {" "}
          </div>
        )}
      </div>
    );
    return createPortal(node, document.body);
  }

  return <></>;
};

let __modalZ = 0;
function nextModalZIndex() {
  // base is token backdrop 1040; each open modal gets higher z-index
  if (!__modalZ) __modalZ = 1040;
  __modalZ += 5;
  return __modalZ;
}

export const ModalBody = ({
  children,
  className = "",
  showCloseButton = false,
  closeButtonProps = {},
  closeRowClassName = "",
}) => {
  const ctx = useContext(ModalContext);
  const modalBodyRef = useRef(null);

  useEffect(() => handleScroll(), []);

  const handleScroll = () => {
    const body = modalBodyRef.current;

    if (body) {
      const { scrollTop, clientHeight, scrollHeight } = body;

      if (!(scrollHeight > clientHeight)) return;

      const footer = document.getElementById("footer-modal");
      const header = document.getElementById("modal-header");

      if (scrollTop === 0) {
        footer?.classList?.add(`${styles["footer-modal--border-top"]}`);
        header?.classList?.remove(
          `${styles["modal-content__header--border-bottom"]}`,
        );
      } else {
        header?.classList?.add(
          `${styles["modal-content__header--border-bottom"]}`,
        );
      }

      if (scrollTop > clientHeight) {
        footer?.classList?.remove(`${styles["footer-modal--border-top"]}`);
      } else {
        footer?.classList?.add(`${styles["footer-modal--border-top"]}`);
      }
    }
  };

  return (
    <div
      ref={modalBodyRef}
      className={`${styles["body"]} ${className && className}`}
      onScroll={handleScroll}
    >
      {showCloseButton && ctx?.onClose && (
        <div
          className={`${styles["modal-body-close-row"]} ${closeRowClassName}`.trim()}
        >
          <ModalClose {...closeButtonProps} />
        </div>
      )}
      {children}
    </div>
  );
};

export const ModalFooter = ({ children }) => (
  <div id="footer-modal" className={`${styles["footer-modal"]}`}>
    <div className={`${styles["footer-modal__buttons"]}`}>{children}</div>
  </div>
);
